import type React from 'react';
import { useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom-v5-compat';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AUTOMATION_WORKFLOW_DRAWER_OPENED } from '../../../constants/analytics-events';
import { getAutomationWorkflowPath, getAutomationWorkflowsSettingsPath } from '../../../routes/constants';
import { Segment } from '../../../services/analytics-services/segment';
import { LabsFeedbackBanner } from '../labs/components/labs-feedback-banner/labs-feedback-banner';
import { LABS_FEATURES } from '../labs/labs-feature-constants';
import type { LabsFeature } from '../labs/typedefs';
import { AutomationWorkflows } from '../../../constants/features';

import type { Workflow, WorkflowFormData } from './types';
import { WorkflowsProvider, useWorkflowsContext } from './context';
import { useWorkflows } from './hooks/use-workflows';
import { WorkflowDrawer } from './components/workflow-drawer';
import { WorkflowDrawerZoomable } from './components/workflow-drawer-zoomable';
import { WorkflowList } from './components/workflow-list';
import { WorkflowsEmptyState } from './components/workflows-empty-state';
import { messages } from './messages';
import styles from './styles.module.less';

// TODO: When the tab view is removed and AutomationWorkflows gets its own route,
// replace this local drawer state with React Router sub-routes:
//   open:  history.push(`${path}/new`) / history.push(`${path}/${workflow.id}`)
//   close: history.push(path)
//   check: <Route path={`${path}/new`}> / <Route path={`${path}/:id`}>

const automationWorkflowsFeature = LABS_FEATURES.find((f: LabsFeature) => f.id === AutomationWorkflows);

const AutomationWorkflowsTabContent: React.FC = () => {
  const { sortedWorkflows, isError, addWorkflow, updateWorkflow, deleteWorkflow, toggleWorkflowActive } = useWorkflows();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerWorkflow, setDrawerWorkflow] = useState<Workflow | null>(null);

  const handleOpenAddDrawer = useCallback(() => {
    setDrawerWorkflow(null);
    setIsDrawerOpen(true);
    Segment.trackEvent(AUTOMATION_WORKFLOW_DRAWER_OPENED, { mode: 'create' });
  }, []);

  const handleOpenEditDrawer = useCallback((workflow: Workflow) => {
    setDrawerWorkflow(workflow);
    setIsDrawerOpen(true);
    Segment.trackEvent(AUTOMATION_WORKFLOW_DRAWER_OPENED, {
      mode: 'edit',
      workflowId: workflow.id,
      workflowStatus: workflow.automationWorkflowStatus,
      entityType: workflow.entityType,
    });
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerWorkflow(null);
  }, []);

  const handleSave = useCallback(
    (formData: WorkflowFormData) => {
      const operation = drawerWorkflow ? updateWorkflow(drawerWorkflow.id, formData) : addWorkflow(formData);
      operation.then(handleCloseDrawer).catch(() => {
        // error handled by use-workflows (toast + rollback); drawer stays open for retry
      });
    },
    [drawerWorkflow, addWorkflow, updateWorkflow, handleCloseDrawer],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteWorkflow(id).finally(handleCloseDrawer);
    },
    [deleteWorkflow, handleCloseDrawer],
  );

  const { isZoomableDrawerOpen, workflowId, openZoomableWorkflowDrawer, closeZoomableWorkflowDrawer } = useWorkflowsContext();
  const navigate = useNavigate();

  const handleOpenZoomableDrawer = useCallback(
    (workflow: Workflow) => {
      openZoomableWorkflowDrawer(workflow.id);
      navigate(getAutomationWorkflowPath(workflow.id));
    },
    [openZoomableWorkflowDrawer, navigate],
  );

  const handleCloseZoomableDrawer = useCallback(() => {
    closeZoomableWorkflowDrawer();
    navigate(getAutomationWorkflowsSettingsPath());
  }, [closeZoomableWorkflowDrawer, navigate]);

  const zoomableWorkflow: Workflow | null = workflowId != null ? (sortedWorkflows.find((w) => w.id === workflowId) ?? null) : null;

  return (
    <FlexColumn className={styles['workflows-tab-content']} gap={Gap.Space16}>
      {isError && (
        <BannerMessageV2 content={<Text textType={TextType.Paragraph} value={messages.loadError} />} status={BannerStatus.Error} />
      )}
      {automationWorkflowsFeature && (
        <LabsFeedbackBanner
          content={<Text textType={TextType.Paragraph} value={messages.feedbackBannerContent} />}
          feature={automationWorkflowsFeature}
        />
      )}
      <FlexRow className={styles['tab-actions']} gap={Gap.Space8}>
        <Button onClick={handleOpenAddDrawer} theme={THEME_CONSTANTS.primary}>
          <Text textType={TextType.Caption} value={messages.createNew} />
        </Button>
      </FlexRow>
      {sortedWorkflows.length > 0 ? (
        <WorkflowList
          onOpenEditDrawer={handleOpenEditDrawer}
          onOpenZoomableDrawer={handleOpenZoomableDrawer}
          onToggleActive={toggleWorkflowActive}
          workflows={sortedWorkflows}
        />
      ) : (
        <WorkflowsEmptyState onCreateWorkflow={handleOpenAddDrawer} />
      )}
      {isDrawerOpen && (
        <WorkflowDrawer
          allWorkflows={sortedWorkflows}
          onClose={handleCloseDrawer}
          onDelete={handleDelete}
          onSave={handleSave}
          workflow={drawerWorkflow}
        />
      )}
      {isZoomableDrawerOpen && <WorkflowDrawerZoomable onClose={handleCloseZoomableDrawer} workflow={zoomableWorkflow} />}
    </FlexColumn>
  );
};

export const AutomationWorkflowsTab: React.FC = () => (
  <WorkflowsProvider>
    <AutomationWorkflowsTabContent />
  </WorkflowsProvider>
);
